import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { TextAreaChangeEvent } from '@progress/kendo-react-inputs';
import Button from '@components-clean/core/button';
import { InfoField } from '@components-clean/core/info-field';
import { TextArea } from '@components-clean/core/text-area';
import frontEndMessageHub, { IFrontEndMessageHub } from '@utils-clean/front-end-message-hub';
import { useAuthorization } from '@hooks/index';
import { ShowError } from '@utils/notification';
import { addRiskPocComment, addRiskPocCommentVariables } from '@queries/generated/risk-assessment.generated';
import { RISK_POC_COMMENT } from '@queries/risk-assessment';

export const RiskCommentsPocPage = () => {
    const [value, setValue] = useState('');
    const user = useAuthorization();
    const [messages, setMessages] = useState<string[]>([]);

    const frontEndHubRef = useRef<IFrontEndMessageHub>(frontEndMessageHub('risk-hub'));
    const { hubConnection, connectToHub, disconnectFromHub } = frontEndHubRef.current;
    const [addComment] = useMutation<addRiskPocComment, addRiskPocCommentVariables>(RISK_POC_COMMENT);

    const handleTextChange = (event: TextAreaChangeEvent) => {
        setValue(event.target.value ?? '');
    };

    const handleSubmit = () => {
        addComment({
            variables: {
                riskComment: {
                    id: `commentId for comment with content: ${value}`,
                    publisherUserId: user!.id!,
                    parentId: '123456789',
                },
            },
        })
            .then(() => {
                setValue('');
            })
            .catch(() => {
                ShowError('Failed to send comment');
            });
    };

    useEffect(() => {
        connectToHub('123456789').catch(() => {});

        hubConnection.on('CommentAdded', (data) => {
            setMessages((prevMessages) => [...prevMessages, data.id]);
        });

        hubConnection.onreconnected(() => {
            connectToHub('123456789').catch(() => {});
        });

        return () => {
            hubConnection.off('RiskCommentAdded');
            disconnectFromHub();
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    return (
        <div className="k-d-flex k-flex-column k-gap-2">
            <InfoField
                message='This PoC will only show new messages sent since user joined, as there is no database hooked up yet.
                The risk comment signalr hub is grouped by risk id. The risk Id this poc is using is "123456789". As the messages do not contain
                comment content, the poc bundles the content into the comment id purely to better show it working.'
                type="Info"
            />
            {messages.map((message) => (
                <div key={message}>{message}</div>
            ))}
            <TextArea
                data-testid="text-area-with-submit-input"
                value={value}
                placeholder="Type a comment"
                onChange={handleTextChange}
            />
            <Button data-testid="text-area-with-submit-button" onClick={handleSubmit} primary>
                Send
            </Button>
        </div>
    );
};
